/**
 * Tabela de tiltímetros do Resumo Técnico — GUPM-270.
 *
 * O tiltímetro não cabe na tabela principal: ela tem uma coluna de leitura e três
 * de limiar por linha, e ele mede DUAS inclinações independentes. Desenho definido
 * pelo dono em 12/08/2026 — uma linha por instrumento, os dois eixos em colunas,
 * tabela própria abaixo da principal, dentro da seção de cada barragem.
 *
 * Um só conjunto de colunas de limiar serve os dois eixos porque as bandas são
 * simétricas e iguais em A e B. Quando não são, `limites` vem `null` e as colunas
 * ficam em "—": mostrar ali os números de um eixo ao lado do valor do outro
 * convidaria a comparação errada numa tela de segurança de barragem.
 */
import { ehAnomaliaAcionavel } from '@/domain/instrumentos/nivel-exibido';
import { NIVEL_EXIBIDO_UI } from '@/ui';
import type { Instrument } from '@/domain';
import type { EixoTilt, LinhaTiltimetro } from '@/domain/instrumentos/tilt';

export interface TabelaTiltimetrosProps {
  linhas: LinhaTiltimetro[];
  /** Retirados viram nota de rodapé, não linha (convenção da tabela principal). */
  retirados: Instrument[];
  onFluxo: (instrumentId: string) => void;
  /** Modo mapa: clicar no código destaca o pino. */
  onSelect?: (id: string) => void;
  selectedId?: string | null;
}

/** Número em pt-BR com casas fixas; `null` vira travessão. */
const fmt = (n: number | null, casas: number) =>
  n == null ? '—' : n.toFixed(casas).replace('.', ',');

/** Limiar em MÓDULO — o `±` diz que a régua vale nos dois sentidos do eixo. */
const fmtLimite = (n: number | null) => (n == null ? '—' : `±${fmt(n, 2)}`);

export default function TabelaTiltimetros({
  linhas, retirados, onFluxo, onSelect, selectedId = null,
}: TabelaTiltimetrosProps) {
  if (linhas.length === 0 && retirados.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-jf-muted">
          Tiltímetros
        </h3>
        {/* A unidade fica no cabeçalho da tabela, não repetida em 6 células. */}
        <span className="text-[10px] text-jf-dim">inclinação em graus (°)</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-jf-border text-left text-jf-dim">
              <th className="pb-2 pr-3 font-semibold">Instrumento</th>
              {/* SEM coluna "Seção" (decisão do dono em 12/08/2026): o inventário v2
                  não informa seção de tiltímetro. Medido nos 10 ativos — 2 em `null`,
                  6 em string VAZIA e 2 em `'1'`, valor que nem bate com a Carta (ela
                  diz seção "D" para o TILT-01 da Barragem C). Contra INA e PZ, com
                  28/28 e 14/14 preenchidas em 9 seções distintas cada.
                  Coluna vazia em 8 de 10 linhas afirma "existe dado e está
                  escondido" — pior que não ter a coluna, e custa largura numa tabela
                  que já rola na horizontal no layout de 3 colunas. */}
              {/* Só "Eixo A"/"Eixo B": nomear a direção física (N-S / L-O)
                  afirmaria como o sensor está montado, e isso não está verificado
                  em fonte nenhuma — a v2 entrega `axisA`/`axisB` e a Carta escreve
                  "Eixo A"/"Eixo B". Se algum sensor estiver rotacionado, um rótulo
                  de direção mandaria o RT olhar para o lado errado. */}
              <th className="pb-2 pr-3 text-right font-semibold">Eixo A</th>
              <th className="pb-2 pr-3 text-right font-semibold">Eixo B</th>
              <th className="pb-2 pr-3 text-right font-semibold">Temp. (°C)</th>
              <th className="pb-2 pr-3 text-right font-semibold text-level-atencao">Atenção</th>
              <th className="pb-2 pr-3 text-right font-semibold text-level-alerta">Alerta</th>
              <th className="pb-2 pr-3 text-right font-semibold text-level-emergencia">Emergência</th>
              <th className="pb-2 pr-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <LinhaTilt
                key={l.instrument.id}
                linha={l}
                onFluxo={onFluxo}
                onSelect={onSelect}
                selected={selectedId === l.instrument.id}
              />
            ))}
            {retirados.length > 0 && (
              <tr>
                <td colSpan={8} className="py-2 text-[10px] italic text-jf-dim">
                  {`Retirados (histórico preservado, fora do monitoramento): ${retirados
                    .map((i) => i.code)
                    .join(', ')}.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface LinhaProps {
  linha: LinhaTiltimetro;
  onFluxo: (instrumentId: string) => void;
  onSelect?: (id: string) => void;
  selected: boolean;
}

/** Célula de um eixo: valor ASSINADO, colorido pelo nível do PRÓPRIO eixo. */
function CelulaEixo({ eixo }: { eixo: EixoTilt | null }) {
  const ui = eixo?.level ? NIVEL_EXIBIDO_UI[eixo.level] : null;
  return (
    <td
      className={`py-1.5 pr-3 text-right font-mono font-bold ${eixo == null ? 'text-jf-dim' : ''}`}
      style={ui ? { color: ui.color } : undefined}
    >
      {fmt(eixo?.value ?? null, 2)}
    </td>
  );
}

function LinhaTilt({ linha, onFluxo, onSelect, selected }: LinhaProps) {
  const { instrument: i, limites } = linha;
  const ui = NIVEL_EXIBIDO_UI[linha.nivel];
  // Mesma regra da tabela principal. `ehAnomaliaAcionavel` e não `ehNivelMedido`: a
  // guarda do GUPM-247 continua impedindo "sem faixa" de virar convite a abrir
  // emergência, mas o SEM_SINAL entra — ele tem protocolo próprio (`TARP-PS-TILT`).
  const anomalia = ehAnomaliaAcionavel(linha.nivel);

  return (
    <tr
      aria-selected={onSelect ? selected : undefined}
      className={selected ? 'bg-jf-yellow/10' : undefined}
      style={anomalia && !selected ? { background: ui.bg } : undefined}
    >
      <td className="py-1.5 pr-3">
        {onSelect ? (
          <button
            type="button"
            onClick={() => onSelect(i.id)}
            aria-label={`Destacar ${i.code} no mapa`}
            className="cursor-pointer font-mono font-bold underline underline-offset-2"
            style={{ color: ui.color }}
          >
            {i.code}
          </button>
        ) : (
          <span className="font-mono font-bold" style={{ color: ui.color }}>{i.code}</span>
        )}
      </td>
      <CelulaEixo eixo={linha.eixoA} />
      <CelulaEixo eixo={linha.eixoB} />
      {/* Temperatura é dado do sensor, não grandeza geotécnica: sem cor de nível,
          porque não existe faixa contra a qual classificá-la. */}
      <td className="py-1.5 pr-3 text-right font-mono text-jf-muted">
        {fmt(linha.temperatura, 1)}
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-level-atencao">
        {fmtLimite(limites?.atencao ?? null)}
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-level-alerta">
        {fmtLimite(limites?.alerta ?? null)}
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-level-emergencia">
        {fmtLimite(limites?.emergencia ?? null)}
      </td>
      <td className="py-1.5 pr-3">
        {anomalia ? (
          <button
            type="button"
            onClick={() => onFluxo(i.id)}
            aria-label={`Abrir fluxo TARP para ${i.code}`}
            title="Abrir fluxo TARP"
            className="rounded-full border px-2 py-0.5 text-[10px] font-black transition hover:brightness-125"
            style={{ color: ui.color, borderColor: ui.color, background: ui.bg }}
          >
            {ui.label} →
          </button>
        ) : (
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${
              ui.outline ? 'border-dashed' : ''
            }`}
            style={{ color: ui.color, borderColor: ui.border, background: ui.bg }}
          >
            {ui.label}
          </span>
        )}
      </td>
    </tr>
  );
}
